import {
  signUp, signIn, signOut, getCurrentUser, onAuthChange,
  resetPassword, updatePassword, isPasswordRecoverySession, clearRecoveryHash,
} from '../services/api/auth';
import { migrateLocalCatalogsToSupabase, hasMigrationRun } from '../services/api/migration';
import type { AuthUser } from '../services/api/auth';
import { RateLimiter } from '../services/security/RateLimiter';
import { buildAuthModalHtml } from './AuthModalTemplate';

type AuthMode = 'login' | 'signup' | 'forgot' | 'reset';

export class AuthModal {
  private modal: HTMLElement;
  private overlay: HTMLElement;
  private titleEl: HTMLElement;
  private errorEl: HTMLElement;

  private emailInput: HTMLInputElement;
  private passwordInput: HTMLInputElement;
  private nameInput: HTMLInputElement;
  private newPasswordInput: HTMLInputElement;
  private submitBtn: HTMLButtonElement;
  private toggleBtn: HTMLButtonElement;
  private forgotLink: HTMLElement;
  private backBtn: HTMLElement;

  private mode: AuthMode = 'login';
  private user: AuthUser | null = null;
  private onAuthChangeCb: (user: AuthUser | null) => void;
  private onEventCb: ((type: string, detail: string) => void) | null;
  private unsubscribe: (() => void) | null = null;
  private rateLimiter: RateLimiter;

  constructor(
    container: HTMLElement,
    onAuthChange: (user: AuthUser | null) => void,
    onEvent?: (type: string, detail: string) => void,
  ) {
    this.onAuthChangeCb = onAuthChange;
    this.onEventCb = onEvent ?? null;
    this.rateLimiter = new RateLimiter({ windowMs: 15000, maxRequests: 3 });

    this.overlay = document.createElement('div');
    this.overlay.className = 'auth-overlay hidden';

    this.modal = document.createElement('div');
    this.modal.className = 'auth-modal';
    this.modal.innerHTML = buildAuthModalHtml();

    container.appendChild(this.overlay);
    container.appendChild(this.modal);

    this.titleEl = this.modal.querySelector('#authModalTitle')!;
    this.errorEl = this.modal.querySelector('#authError')!;
    this.emailInput = this.modal.querySelector('#authEmailInput') as HTMLInputElement;
    this.passwordInput = this.modal.querySelector('#authPasswordInput') as HTMLInputElement;
    this.nameInput = this.modal.querySelector('#authNameInput') as HTMLInputElement;
    this.newPasswordInput = this.modal.querySelector('#authNewPasswordInput') as HTMLInputElement;
    this.submitBtn = this.modal.querySelector('#authSubmitBtn') as HTMLButtonElement;
    this.toggleBtn = this.modal.querySelector('#authToggleBtn') as HTMLButtonElement;
    this.forgotLink = this.modal.querySelector('#authForgotLink')!;
    this.backBtn = this.modal.querySelector('#authModalBack')!;

    const closeBtn = this.modal.querySelector('#authModalClose')!;

    this.overlay.addEventListener('click', () => this.close());
    closeBtn.addEventListener('click', () => this.close());
    this.backBtn.addEventListener('click', () => this.goToLogin());
    this.forgotLink.addEventListener('click', () => this.goToForgot());
    this.toggleBtn.addEventListener('click', () => this.toggleMode());
    this.submitBtn.addEventListener('click', () => this.handleSubmit());

    this.emailInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') this.handleSubmit(); });
    this.passwordInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') this.handleSubmit(); });
    this.nameInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') this.handleSubmit(); });
    this.newPasswordInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') this.handleSubmit(); });

    this.initSession();
  }

  private async initSession(): Promise<void> {
    this.user = await getCurrentUser();
    if (this.user) {
      this.onAuthChangeCb(this.user);
    }

    this.unsubscribe = onAuthChange((user) => {
      this.user = user;
      this.onAuthChangeCb(user);
    });

    // Check for password recovery session on startup
    if (isPasswordRecoverySession()) {
      this.mode = 'reset';
      this.updateMode();
      this.open();
    }
  }

  destroy(): void {
    if (this.unsubscribe) this.unsubscribe();
    this.overlay.remove();
    this.modal.remove();
  }

  open(mode?: AuthMode): void {
    if (mode) this.mode = mode;
    this.updateMode();
    this.overlay.classList.remove('hidden');
    this.modal.classList.add('open');
    setTimeout(() => this.emailInput.focus(), 100);
  }

  close(): void {
    this.overlay.classList.add('hidden');
    this.modal.classList.remove('open');
    this.clearError();
  }

  isOpen(): boolean {
    return !this.overlay.classList.contains('hidden');
  }

  getUser(): AuthUser | null {
    return this.user;
  }

  async logout(): Promise<void> {
    const email = this.user?.email;
    try {
      await signOut();
    } catch {
      // Se signOut falhar (offline/Supabase indisponível), limpa local mesmo assim
    }
    this.user = null;
    this.onAuthChangeCb(null);
    if (email) this.emit('auth.logout', `Logout: ${email}`);
  }

  private emit(type: string, detail: string): void {
    this.onEventCb?.(type, detail);
  }

  private toggleMode(): void {
    if (this.mode === 'login') {
      this.mode = 'signup';
    } else if (this.mode === 'signup') {
      this.mode = 'login';
    }
    this.updateMode();
  }

  private goToForgot(): void {
    this.mode = 'forgot';
    this.updateMode();
  }

  private goToLogin(): void {
    this.mode = 'login';
    this.updateMode();
  }

  private updateMode(): void {
    const nameGroup = this.modal.querySelector('#authNameGroup')!;
    const passwordGroup = this.modal.querySelector('#authPasswordGroup')!;
    const newPasswordGroup = this.modal.querySelector('#authNewPasswordGroup')!;
    const resetInfo = this.modal.querySelector('#authResetInfo')!;

    this.backBtn.classList.toggle('hidden', this.mode !== 'forgot' && this.mode !== 'reset');
    nameGroup.classList.toggle('hidden', this.mode !== 'signup');
    passwordGroup.classList.toggle('hidden', this.mode === 'forgot' || this.mode === 'reset');
    newPasswordGroup.classList.toggle('hidden', this.mode !== 'reset');
    resetInfo.classList.toggle('hidden', this.mode !== 'forgot');
    this.forgotLink.classList.toggle('hidden', this.mode !== 'login');
    this.toggleBtn.classList.toggle('hidden', this.mode === 'forgot' || this.mode === 'reset');

    if (this.mode === 'login') {
      this.titleEl.textContent = 'Entrar';
      this.submitBtn.textContent = 'Entrar';
      this.toggleBtn.textContent = 'Criar conta';
    } else if (this.mode === 'signup') {
      this.titleEl.textContent = 'Criar Conta';
      this.submitBtn.textContent = 'Cadastrar';
      this.toggleBtn.textContent = 'Já tenho conta';
    } else if (this.mode === 'forgot') {
      this.titleEl.textContent = 'Redefinir Senha';
      this.submitBtn.textContent = 'Enviar link';
    } else if (this.mode === 'reset') {
      this.titleEl.textContent = 'Nova Senha';
      this.submitBtn.textContent = 'Atualizar senha';
    }

    this.clearError();
  }

  private async handleSubmit(): Promise<void> {
    this.clearError();

    if (this.mode === 'reset') {
      await this.handleResetPassword();
      return;
    }

    if (this.mode === 'forgot') {
      await this.handleForgotPassword();
      return;
    }

    // Rate limiting: max 3 tentativas a cada 15s
    const rateKey = `auth:${this.emailInput.value.trim().toLowerCase()}`;
    if (!this.rateLimiter.allow(rateKey)) {
      this.showError(`Muitas tentativas. Aguarde ${Math.ceil(this.rateLimiter.timeUntilReset(rateKey) / 1000)}s.`);
      return;
    }

    const email = this.emailInput.value.trim();
    const password = this.passwordInput.value;

    if (!email) { this.showError('Informe o email'); return; }
    if (!password) { this.showError('Informe a senha'); return; }

    if (this.mode === 'signup') {
      const name = this.nameInput.value.trim();
      if (!name) { this.showError('Informe seu nome'); return; }
      if (password.length < 6) { this.showError('Senha deve ter no mínimo 6 caracteres'); return; }
    }

    this.submitBtn.disabled = true;
    this.submitBtn.textContent = 'Aguarde...';

    try {
      if (this.mode === 'login') {
        this.user = await signIn(email, password);
        this.emit('auth.login', `Login: ${this.user.email}`);
      } else {
        this.user = await signUp(email, password, this.nameInput.value.trim());
        this.emit('auth.signup', `Cadastro: ${this.user.email}`);
      }

      if (!hasMigrationRun()) {
        const result = await migrateLocalCatalogsToSupabase();
        if (result.migrated > 0) {
          alert(`${result.migrated} catálogo(s) migrado(s) para a nuvem com sucesso!`);
        }
      }

      this.onAuthChangeCb(this.user);
      this.close();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      const code = typeof err === 'object' && err !== null ? (err as { code?: string; status?: string }).code ?? (err as { status?: string }).status ?? '' : '';
      if (code === 'over_email_send_rate_limit' || msg.includes('rate_limit') || msg.includes('rate limit')) {
        this.showError('Muitas tentativas. Aguarde 1 minuto e tente novamente.');
        this.emit('auth.login.failed', `Rate limit: ${email}`);
      } else if (msg.includes('already registered') || msg.includes('already exists')) {
        this.showError('Email já cadastrado. Faça login.');
        this.emit('auth.login.failed', `Tentativa cadastro email existente: ${email}`);
      } else if (msg.includes('Invalid login')) {
        this.showError('Email ou senha inválidos.');
        this.emit('auth.login.failed', `Tentativa login inválido: ${email}`);
      } else {
        this.showError(msg);
        this.emit('auth.login.failed', `Erro: ${msg}`);
      }
    } finally {
      this.submitBtn.disabled = false;
      this.submitBtn.textContent = this.mode === 'login' ? 'Entrar' : 'Cadastrar';
    }
  }

  private async handleForgotPassword(): Promise<void> {
    const email = this.emailInput.value.trim();
    if (!email) { this.showError('Informe seu email'); return; }

    this.submitBtn.disabled = true;
    this.submitBtn.textContent = 'Enviando...';

    try {
      await resetPassword(email);
      this.emit('auth.password.reset', `Link enviado para: ${email}`);
      this.mode = 'forgot';
      this.updateMode();
      this.emailInput.value = '';
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      const code = typeof err === 'object' && err !== null ? (err as { code?: string; status?: string }).code ?? '' : '';
      if (code === 'over_email_send_rate_limit' || msg.includes('rate_limit') || msg.includes('rate limit')) {
        this.showError('Muitas solicitações. Aguarde 1 minuto e tente novamente.');
      } else {
        this.showError(msg);
      }
    } finally {
      this.submitBtn.disabled = false;
      this.submitBtn.textContent = 'Enviar link';
    }
  }

  private async handleResetPassword(): Promise<void> {
    const password = this.newPasswordInput.value;
    if (!password) { this.showError('Informe a nova senha'); return; }
    if (password.length < 6) { this.showError('Mínimo 6 caracteres'); return; }

    this.submitBtn.disabled = true;
    this.submitBtn.textContent = 'Atualizando...';

    try {
      await updatePassword(password);
      clearRecoveryHash();
      this.emit('auth.password.updated', 'Senha redefinida com sucesso');
      await signOut();
      alert('Senha atualizada com sucesso! Faça login novamente.');
      this.mode = 'login';
      this.updateMode();
      this.close();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      this.showError(msg || 'Erro ao atualizar senha');
    } finally {
      this.submitBtn.disabled = false;
      this.submitBtn.textContent = 'Atualizar senha';
    }
  }

  private showError(msg: string): void {
    this.errorEl.textContent = msg;
    this.errorEl.classList.remove('hidden');
  }

  private clearError(): void {
    this.errorEl.classList.add('hidden');
    this.errorEl.textContent = '';
  }
}
